import { FC, useState, useEffect, useMemo } from "react";
import {
  FormDialog,
  FormDialogCancel,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  Input,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Badge,
  Skeleton,
  TruncatedText,
  MultiSelectFilter,
} from "@capacit-com/lara-law-shared-ui";
import { Search, ChevronLeft, ChevronRight } from "lucide-react";
import useLegislation from "@/hooks/useLegislation";
import useLegislationFilters from "@/hooks/useLegislationFilters";
import type { Legislation } from "@/models/legislation/Legislation";
import type { ColumnFiltersState, SortingState } from "@tanstack/react-table";
import {
  DataSource,
  getDisplayName,
  getInvalidLabel,
  normalizeDataSource,
} from "@/config/datasources";
import { dateValue } from "@/utils/legislationMetadataUtils";
import { isConsolidationEligibleDocumentType } from "@/utils/legislationDocumentTypeUtils";
import { cn } from "@/lib/utils";
import { getConsolidationProgressDisplay } from "@/lib/consolidationProgressDisplay";
import { getRetsinformationMetadataFromLegislation } from "@/lib/consolidationLegislationUtils";
import ConsolidationProgressBadge from "@/pages/legislation/components/ConsolidationProgressBadge";
import {
  buildFilterOptions,
  formatFormerNamesPreview,
} from "@/pages/legislation/utils/ministryFilterOptions";

export interface SelectedLegislation {
  externalId: string;
  datasource: string;
  title: string;
}

interface LegislationPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (documents: SelectedLegislation[]) => void;
  excludeIds?: string[];
  /** Adds a "Konsolidering" progress column (same logic as the legislation
   *  overview table). Off by default so existing consumers are unchanged. */
  showConsolidationStatus?: boolean;
  /**
   * Only consolidation-eligible documents are selectable: Retsinformation
   * Lov (10), LBK (30) or BEK (60) — the chain-starting boundary types per
   * consolidation/docs/context/02-document-types.md. Also seeds the datasource
   * filter to Retsinformation. Off by default.
   */
  onlyConsolidationEligible?: boolean;
}

const LegislationPickerDialog: FC<LegislationPickerDialogProps> = ({
  open,
  onOpenChange,
  onSelect,
  excludeIds = [],
  showConsolidationStatus = false,
  onlyConsolidationEligible = false,
}) => {
  const { useGetPaginated } = useLegislation();
  const { useGetFilterConfiguration } = useLegislationFilters();
  const { data: filterConfig } = useGetFilterConfiguration();

  // Local state for table - persisted across dialog opens within the same session
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize] = useState(10);
  const [sorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [selectedItems, setSelectedItems] = useState<
    Map<string, SelectedLegislation>
  >(new Map());

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPageIndex(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Only reset selection when dialog opens, preserve search and filters
  useEffect(() => {
    if (open) {
      setSelectedItems(new Map());
      setPageIndex(0);
    }
  }, [open]);

  /** Retsinformation Lov/LBK/BEK — the chain-starting types (10/30/60). */
  const isConsolidationEligible = (item: Legislation): boolean => {
    const meta = getRetsinformationMetadataFromLegislation(item);
    return (
      meta !== null && isConsolidationEligibleDocumentType(meta.documentTypeId)
    );
  };

  // The filter-config key for Retsinformation (config keys may differ in
  // casing from the DataSource enum value).
  const retsinformationSourceKey = useMemo(() => {
    const keys = Object.keys(filterConfig?.sources ?? {});
    return (
      keys.find((k) => normalizeDataSource(k) === DataSource.Retsinformation) ??
      DataSource.Retsinformation
    );
  }, [filterConfig]);

  // Consolidation mode pins the server-side filters (datasource +
  // chain-starting document types, cf. consolidation/docs/context/
  // 02-document-types.md). They live outside the user-editable filter state,
  // are merged in at query time, and their controls are hidden below — the
  // user cannot change or even see them; only Ministerium remains pickable.
  const pinnedFilters: ColumnFiltersState = useMemo(
    () =>
      onlyConsolidationEligible
        ? [
            { id: "datasource", value: [retsinformationSourceKey] },
            {
              id: "documentType",
              value: ["Lov", "Lovbekendtgørelse", "Bekendtgørelse"],
            },
          ]
        : [],
    [onlyConsolidationEligible, retsinformationSourceKey]
  );

  const effectiveFilters: ColumnFiltersState = useMemo(() => {
    if (pinnedFilters.length === 0) return columnFilters;
    const pinnedIds = new Set(pinnedFilters.map((f) => f.id));
    return [
      ...pinnedFilters,
      ...columnFilters.filter((f) => !pinnedIds.has(f.id)),
    ];
  }, [pinnedFilters, columnFilters]);

  // Get selected datasources from filters (pinned filters included)
  const selectedDatasources = useMemo(() => {
    const entry = effectiveFilters.find((f) => f.id === "datasource");
    if (!entry) return [];
    const value = entry.value as string[];
    return Array.isArray(value) ? value : [];
  }, [effectiveFilters]);

  // Build datasource options from filter config
  const datasourceOptions = useMemo(() => {
    if (!filterConfig?.sources) return [];
    return Object.entries(filterConfig.sources).map(([key, source]) => ({
      label: source.displayName || key,
      value: key,
    }));
  }, [filterConfig]);

  // Build child filter options based on selected datasource
  const childFilterOptions = useMemo(() => {
    if (selectedDatasources.length !== 1 || !filterConfig?.sources) return {};

    const sourceConfig = filterConfig.sources[selectedDatasources[0]];
    if (!sourceConfig?.filters) return {};

    const options: Record<
      string,
      { label: string; value: string; secondaryLabel?: string }[]
    > = {};
    for (const [key, filter] of Object.entries(sourceConfig.filters)) {
      if (filter.options && filter.options.length > 0) {
        options[key] = buildFilterOptions(
          key,
          filter.options,
          filter.optionAliases
        ).map((opt) => ({
          label: opt.label,
          value: opt.value,
          secondaryLabel:
            opt.aliases && opt.aliases.length > 0
              ? `Inkluderer også: ${formatFormerNamesPreview(opt.aliases).preview}`
              : undefined,
        }));
      }
    }
    return options;
  }, [selectedDatasources, filterConfig]);

  // Fetch data
  const {
    data: paginationData,
    isLoading,
    isFetching,
  } = useGetPaginated(
    {
      pageNumber: pageIndex + 1,
      pageSize,
      sortBy: sorting[0]?.id || "",
      sortAscending: sorting[0]?.desc === false,
      search: debouncedSearch,
      filters: effectiveFilters,
    },
    selectedDatasources.length > 0 ? selectedDatasources : undefined
  );

  const data = paginationData?.data || [];
  const totalCount = paginationData?.totalCount ?? 0;
  const pageCount =
    paginationData?.totalPages ?? Math.ceil(totalCount / pageSize);

  // Handle row selection
  const toggleSelection = (item: Legislation) => {
    const key = `${item.externalId}|${item.datasource}`;

    setSelectedItems((prev) => {
      const next = new Map(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.set(key, {
          externalId: item.externalId,
          datasource: item.datasource,
          title: item.title,
        });
      }
      return next;
    });
  };

  const isItemSelected = (item: Legislation) => {
    const key = `${item.externalId}|${item.datasource}`;
    return selectedItems.has(key);
  };

  const isItemExcluded = (item: Legislation) => {
    const key = `${item.externalId}|${item.datasource}`;
    return excludeIds.includes(key);
  };

  // Handle datasource filter change
  const handleDatasourceChange = (values: string[]) => {
    setColumnFilters((prev) => {
      // Remove datasource filter and all child filters when datasource changes
      const filtered = prev.filter((f) =>
        f.id === "datasource"
          ? false
          : // Also remove child filters when datasource changes
            !Object.keys(childFilterOptions).includes(f.id)
      );

      if (values.length > 0) {
        filtered.push({ id: "datasource", value: values });
      }
      return filtered;
    });
    setPageIndex(0);
  };

  // Handle child filter change
  const handleChildFilterChange = (filterId: string, values: string[]) => {
    setColumnFilters((prev) => {
      const filtered = prev.filter((f) => f.id !== filterId);
      if (values.length > 0) {
        filtered.push({ id: filterId, value: values });
      }
      return filtered;
    });
    setPageIndex(0);
  };

  const handleConfirm = () => {
    const documents = Array.from(selectedItems.values()).filter(
      (doc) => !excludeIds.includes(`${doc.externalId}|${doc.datasource}`)
    );
    onSelect(documents);
    onOpenChange(false);
  };

  const getStatus = (item: Legislation) => {
    if (item.isCurrent === null || item.isCurrent === undefined) return "-";
    return item.isCurrent ? "Gældende" : getInvalidLabel(item.datasource);
  };

  return (
    // A ticked document is an unapplied choice; closing asks before dropping it (LAR-726).
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      isDirty={selectedItems.size > 0}
      discardConfirm={{
        title: "Kassér valget?",
        description: "De dokumenter, du har valgt, bliver ikke tilføjet.",
      }}
      size="full"
      className="flex flex-col min-w-0"
    >
      <DialogHeader>
        <DialogTitle>
          {onlyConsolidationEligible
            ? "Vælg lov, LBK eller BEK til konsolidering"
            : "Vælg dokumenter fra kilder"}
        </DialogTitle>
      </DialogHeader>

      {/* Search and Filters */}
      <div className="space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            role="searchbox"
            placeholder="Søg efter titel..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        <div className="flex flex-wrap gap-2">
          {/* Datasource Filter — hidden and locked in consolidation mode */}
          {!onlyConsolidationEligible && (
            <MultiSelectFilter
              title="Kilde"
              options={datasourceOptions}
              selectedValues={selectedDatasources}
              onSelectionChange={handleDatasourceChange}
            />
          )}

          {/* Child filters when single datasource selected. Consolidation
                mode only exposes Ministerium (documentType/status are pinned
                or irrelevant). */}
          {selectedDatasources.length === 1 &&
            Object.entries(childFilterOptions)
              .filter(
                ([key]) => !onlyConsolidationEligible || key === "ministerium"
              )
              .map(([key, options]) => {
                const currentValues =
                  (columnFilters.find((f) => f.id === key)
                    ?.value as string[]) || [];
                const sourceConfig =
                  filterConfig?.sources?.[selectedDatasources[0]];
                const filterDef = sourceConfig?.filters?.[key];
                const displayName = filterDef?.displayName || key;

                return (
                  <MultiSelectFilter
                    key={key}
                    title={displayName}
                    options={options}
                    selectedValues={currentValues}
                    onSelectionChange={(values: string[]) =>
                      handleChildFilterChange(key, values)
                    }
                  />
                );
              })}
        </div>
      </div>

      {/* Table */}
      <div className="table-embed-card themed-scrollbar flex-1 min-w-0 overflow-y-auto overflow-x-hidden min-h-[300px] border rounded-md">
        <Table className="table-fixed w-full">
          <TableHeader className="sticky top-0 z-10 shadow-sm">
            <TableRow>
              <TableHead className="w-10"></TableHead>
              <TableHead className="min-w-0 whitespace-normal">Titel</TableHead>
              <TableHead className="w-40 min-w-0">
                {onlyConsolidationEligible ? "Nr" : "Kilde"}
              </TableHead>
              <TableHead className="w-32">Dato</TableHead>
              <TableHead className="w-24">Status</TableHead>
              {showConsolidationStatus && (
                <TableHead className="w-36">Konsolidering</TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <Skeleton className="h-4 w-4" />
                  </TableCell>
                  <TableCell className="min-w-0 whitespace-normal">
                    <Skeleton className="h-4 w-full max-w-full" />
                  </TableCell>
                  <TableCell className="min-w-0 align-middle">
                    <Skeleton className="h-5 w-full max-w-36" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-20" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-16" />
                  </TableCell>
                  {showConsolidationStatus && (
                    <TableCell>
                      <Skeleton className="h-4 w-24" />
                    </TableCell>
                  )}
                </TableRow>
              ))
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={showConsolidationStatus ? 6 : 5}
                  className="text-center py-8"
                >
                  <p className="text-muted-foreground">
                    Ingen resultater fundet
                  </p>
                </TableCell>
              </TableRow>
            ) : (
              data.map((item) => {
                const excluded = isItemExcluded(item);
                const ineligible =
                  onlyConsolidationEligible && !isConsolidationEligible(item);
                const unselectable = excluded || ineligible;
                const selected = isItemSelected(item);
                const sourceLabel =
                  filterConfig?.sources?.[item.datasource]?.displayName ??
                  getDisplayName(item.datasource) ??
                  item.datasource;
                // Consolidation mode shows the law number ("LOV nr 636 af
                // 11/06/2024") instead of the datasource — the datasource is
                // pinned to Retsinformation there anyway.
                const lawNumber = onlyConsolidationEligible
                  ? getRetsinformationMetadataFromLegislation(item)?.shortName
                  : undefined;

                return (
                  <TableRow
                    key={`${item.externalId}-${item.datasource}`}
                    className={
                      unselectable
                        ? "opacity-50"
                        : "cursor-pointer hover:bg-muted/50"
                    }
                    onClick={() => !unselectable && toggleSelection(item)}
                  >
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selected}
                        disabled={unselectable}
                        onCheckedChange={() =>
                          !unselectable && toggleSelection(item)
                        }
                      />
                    </TableCell>
                    <TableCell className="min-w-0 align-top whitespace-normal break-words">
                      <span className="line-clamp-2 break-words">
                        {item.title}
                      </span>
                      {excluded && (
                        <span className="text-xs text-muted-foreground ml-2">
                          (allerede tilføjet)
                        </span>
                      )}
                      {!excluded && ineligible && (
                        <span className="text-xs text-muted-foreground ml-2">
                          (kun love, LBK&apos;er og BEK&apos;er kan
                          konsolideres)
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="min-w-0 align-middle">
                      {onlyConsolidationEligible ? (
                        <span
                          className="block truncate text-sm text-muted-foreground"
                          title={lawNumber ?? undefined}
                        >
                          {lawNumber ?? "-"}
                        </span>
                      ) : (
                        <Badge
                          variant="outline"
                          className={cn(
                            "h-5 w-full max-w-full min-w-0 justify-start gap-0 overflow-hidden px-2 py-0",
                            "text-xs font-normal"
                          )}
                        >
                          <TruncatedText
                            text={sourceLabel}
                            tooltipMaxWidth="max-w-xs"
                            className="m-0 min-w-0 w-full text-xs font-normal leading-tight"
                          />
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                      {item.publishedOn ? dateValue(item.publishedOn) : "-"}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          getStatus(item) === "Gældende"
                            ? "default"
                            : "secondary"
                        }
                        className="text-xs"
                      >
                        {getStatus(item)}
                      </Badge>
                    </TableCell>
                    {showConsolidationStatus && (
                      <TableCell>
                        {(() => {
                          const meta =
                            getRetsinformationMetadataFromLegislation(item);
                          const display = getConsolidationProgressDisplay({
                            inboundChanges: meta?.inboundChanges,
                            timelineAmendmentCountAfter:
                              item.timelineAmendmentCountAfter,
                            documentTypeId: meta?.documentTypeId,
                          });
                          if (!display) return "-";
                          return (
                            <ConsolidationProgressBadge
                              display={display}
                              size="xs"
                              showLastVerified={false}
                            />
                          );
                        })()}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">
          {totalCount} resultat{totalCount !== 1 ? "er" : ""} fundet
        </span>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
            disabled={pageIndex === 0 || isFetching}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="min-w-[100px] text-center">
            Side {pageIndex + 1} af {Math.max(1, pageCount)}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPageIndex((p) => Math.min(pageCount - 1, p + 1))}
            disabled={pageIndex >= pageCount - 1 || isFetching}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <DialogFooter className="flex items-center justify-between sm:justify-between">
        <span className="text-sm text-muted-foreground">
          {selectedItems.size} dokument{selectedItems.size !== 1 ? "er" : ""}{" "}
          valgt
        </span>
        <div className="flex gap-2">
          <FormDialogCancel />
          <Button onClick={handleConfirm} disabled={selectedItems.size === 0}>
            Tilføj valgte
          </Button>
        </div>
      </DialogFooter>
    </FormDialog>
  );
};

export default LegislationPickerDialog;
